import { useEffect, useRef } from 'react';
import { Box, Chip, Group, Text, useComputedColorScheme } from '@mantine/core';
import { useQueryState } from '../../app/routes';

/** Screenshots from scripts/splash-shots.mjs (public/splash/{id}-{light,dark}.png), in the order a round goes. */
export const SHOTS: { id: string; label: string; caption: string; alt: string }[] = [
  { id: 'flow', label: 'Flow', caption: 'A column per speech in team colors. Arguments get short titles, and marks show what was extended, turned, dropped or conceded.', alt: 'The flow of a Public Forum round mid-summary: arguments with titles and marks across speech columns, cross-ex notes, and the clock running.' },
  { id: 'paper', label: 'Paper mode', caption: 'The whole screen for the flow, with the clock in a strip. Keyboard first, so nothing needs clicking mid-speech.', alt: 'Paper mode: the flow full screen in dark colors with the timer strip on top.' },
  { id: 'notes', label: 'Speaker notes', caption: 'Quick tags and private notes on each debater and the round, for points and comments later.', alt: 'The notes drawer: round notes and per-speaker tags like Clear and Organized.' },
  { id: 'decide', label: 'Decide', caption: 'An argument map: what each argument claimed, who answered it, and whether it was carried to the end. AI drafts it; the calls are yours.', alt: 'The Decide tab: argument cards showing responses, extensions and weighing, with Alive / ? / Out calls.' },
  { id: 'ballot', label: 'Ballot', caption: 'Your reason for decision and comments, with an assistant that drafts from your calls, edits on request and critiques. Copy each field into Tabroom.', alt: 'The Ballot tab: reason for decision and team comments beside the ballot assistant conversation.' },
  { id: 'dashboard', label: 'Tournaments', caption: "Today's tournament on top with the next round one tap away, then upcoming and previous.", alt: 'The tournaments dashboard: a current tournament with its next round, two upcoming and two previous tournaments.' },
];

/** Swipe (or tap a label) through the app. The shown slide is in the URL (?shot=decide) so it can be linked. */
export const ShotCarousel = () => {
  const scheme = useComputedColorScheme('light');
  const url = useQueryState();
  const current = Math.max(0, SHOTS.findIndex((s) => s.id === url.get('shot')));
  const strip = useRef<HTMLDivElement>(null);
  const settling = useRef<number | null>(null);

  // Show the slide the URL names (on load and when a label is tapped).
  useEffect(() => {
    const el = strip.current;
    if (el && Math.round(el.scrollLeft / el.clientWidth) !== current) el.scrollTo({ left: current * el.clientWidth, behavior: 'smooth' });
  }, [current]);

  // After a swipe settles, record which slide is showing.
  const onScroll = () => {
    if (settling.current) window.clearTimeout(settling.current);
    settling.current = window.setTimeout(() => {
      const el = strip.current;
      if (!el) return;
      const i = Math.round(el.scrollLeft / el.clientWidth);
      if (i !== current) url.set({ shot: i === 0 ? null : SHOTS[i]!.id }, { replace: true });
    }, 120);
  };

  const go = (i: number) => url.set({ shot: i === 0 ? null : SHOTS[i]!.id }, { replace: true });

  return (
    <Box data-testid="shot-carousel">
      <Chip.Group value={SHOTS[current]!.id} onChange={(v) => go(SHOTS.findIndex((s) => s.id === v))}>
        <Group gap={6} mb="sm" role="tablist" aria-label="Screens">
          {SHOTS.map((s) => <Chip key={s.id} value={s.id} size="sm" variant="light" data-testid={`shot-${s.id}`}>{s.label}</Chip>)}
        </Group>
      </Chip.Group>
      <Box
        style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid var(--grid-line)', boxShadow: '0 24px 60px -24px rgba(0, 0, 0, 0.35)' }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowRight') go(Math.min(SHOTS.length - 1, current + 1));
          if (e.key === 'ArrowLeft') go(Math.max(0, current - 1));
        }}
      >
        <div
          ref={strip}
          onScroll={onScroll}
          tabIndex={0}
          aria-roledescription="carousel"
          aria-label="Screenshots of flow"
          className="shot-strip"
          style={{ display: 'flex', overflowX: 'auto', scrollSnapType: 'x mandatory', scrollbarWidth: 'none', outline: 'none' }}
        >
          {SHOTS.map((s, i) => (
            <img
              key={s.id}
              src={`/splash/${s.id}-${scheme}.png`}
              alt={s.alt}
              width={1280}
              height={720}
              loading={i === 0 ? 'eager' : 'lazy'}
              aria-hidden={i !== current}
              style={{ flex: '0 0 100%', width: '100%', height: 'auto', display: 'block', scrollSnapAlign: 'start' }}
              data-testid={i === 0 ? 'splash-shot' : undefined}
            />
          ))}
        </div>
      </Box>
      <Group justify="space-between" mt="sm" wrap="nowrap" align="flex-start">
        <Text size="sm" c="dimmed" maw={760} aria-live="polite" data-testid="shot-caption">{SHOTS[current]!.caption}</Text>
        <Text size="xs" c="dimmed" className="tabular" style={{ flexShrink: 0 }}>{current + 1} / {SHOTS.length}</Text>
      </Group>
    </Box>
  );
};
